import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, catchError, mergeMap, of, switchMap } from 'rxjs';
import { environment } from './../../../environments/environment';
import { RISK_CATEGORIES, riskCategoryLabel, riskLocationLabel, riskSeverityLabel } from './../../shared/components/risk-suggestion-list/risk-suggestion.model';
import { redactName, redactedPatientLabel, shouldRedactNames, NO_NAME } from './../utils/redact-name';
import { FileDownloadService } from './file-download.service';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';
import { UserService } from './user.service';

/**
 * `PatientRiskItem`, GROUP-SCOPED — G1 and all of H of `tasks/risk-management.md`.
 *
 * A copy of `morbidity-mortality.service.ts`: one `BehaviorSubject`, reloaded off
 * `selectedGroup$`, paged to exhaustion, kept live by socket events. §6.J is explicit that this is
 * the FIRST of two services and not a substitute for the second — this one feeds the register on
 * the filters tab (G2) and the PDF export (H); `PatientRiskItemService` (J1) is patient-scoped and
 * feeds the patient file and the clinical timeline. `TaskService` / `PatientTaskService` already
 * split exactly this way and `task-list.component.ts:9-10` imports both side by side.
 *
 * ── TWO THINGS DIFFER FROM THE TEMPLATE, AND BOTH ARE LOAD-BEARING ───────────────────────
 *
 * 1. `patientId` IS NULLABLE (§5c). Most facility issues name no patient — "pharmacy had no
 *    ceftriaxone" is about a pharmacy — and those rows are the point of the register, not an edge
 *    case. Nothing in this file dereferences `item.Patient` without a guard, and the export
 *    renders a missing patient as an em dash rather than inventing a row value.
 *
 * 2. The socket guard is on `groupId`, not on `patientId`. The controller emits into the practice
 *    room, so every item in the group belongs in this list — including the ones with no patient,
 *    which the patient-scoped twin deliberately drops.
 *
 * ── D10 / G6 — WHAT MAY BE FILTERED, AND WHERE ───────────────────────────────────────────
 *
 * `hospitalId`, `hospitalName`, `wardName`, `location`, `category`, `severity`, `status` and
 * `dateOfIssue` are PLAINTEXT columns; `issue`, `quote` and `notes` are `iv:ciphertext` with a
 * random IV per write. An encrypted column can never be matched or grouped in SQL
 * (`lessons.md` §17) — two rows with the same issue text do not even have the same bytes. So the
 * server-side query parameters below cover only the plaintext dimensions, and free-text search
 * over `issue` happens in the LIST COMPONENT, over rows the model has already decrypted. Do not
 * add an `issue` or `notes` parameter to `getRiskItemsByGroup`; it cannot work, and it will fail
 * silently by matching nothing rather than erroring.
 */
@Injectable({
  providedIn: 'root'
})
export class RiskItemService {
  /**
   * Mounted at `router.use('/', patientRouter)` under `/api`, so `/risk-items` here is
   * `routes/clinical/patientRoutes.js`'s `router.get('/risk-items/group/:groupId', ...)`.
   */
  private apiUrl = 'api/risk-items';

  private riskItemsSubject = new BehaviorSubject<any[]>([]);
  riskItems$ = this.riskItemsSubject.asObservable();

  groupId: any;
  currentPage = 1;
  pageSize = 500;
  isLoading = false;

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService,
    private userService: UserService,
    private fileDownloadService: FileDownloadService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1;
          this.riskItemsSubject.next([]);
          return this.loadRiskItems();
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {

    // D4 — `riskItemAdded`. Guarded on `groupId` and NOT on `patientId`: a non-patient item is a
    // first-class row here (see the class header) and dropping it would make the register disagree
    // with itself after a refresh.
    this.socketService.listen('riskItemAdded', 'risk-item-service').subscribe((newItem: any) => {
      if (!newItem || (this.groupId && newItem.groupId !== this.groupId)) return;
      const current = this.riskItemsSubject.getValue();
      // The emitting client receives its own event back. Replace on a known id rather than
      // appending: a duplicated row in a register that gets exported onto a doctor's letterhead is
      // worse than a missed animation.
      const exists = current.some(item => item.id === newItem.id);
      this.riskItemsSubject.next(
        exists ? current.map(item => item.id === newItem.id ? newItem : item) : [...current, newItem]
      );
    });

    this.socketService.listen('riskItemUpdated', 'risk-item-service').subscribe((updated: any) => {
      if (!updated || (this.groupId && updated.groupId !== this.groupId)) return;
      const current = this.riskItemsSubject.getValue();
      const index = current.findIndex(item => item.id === updated.id);
      if (index !== -1) {
        current[index] = updated;
        this.riskItemsSubject.next([...current]);
      }
    });

    // The delete is SOFT server-side (`active: false`) and the emitted payload is the whole row.
    // It still leaves the list: `loadRiskItemsByGroup` filters on `active: true`, so keeping it
    // would make the live list disagree with the list after a refresh.
    this.socketService.listen('riskItemDeleted', 'risk-item-service').subscribe((deleted: any) => {
      if (!deleted) return;
      const current = this.riskItemsSubject.getValue();
      this.riskItemsSubject.next(current.filter(item => item.id !== deleted.id));
    });

    // A renamed or re-validated patient must not leave a stale name on a register that is about to
    // be exported. Only items that actually name this patient are touched — `patientId` is NULL on
    // most rows and `null === undefined` is false, so those are untouched for free.
    this.socketService.listen('patientUpdated', 'risk-item-service').subscribe((updatedPatient: any) => {
      if (!updatedPatient) return;
      const current = this.riskItemsSubject.getValue();
      this.riskItemsSubject.next(current.map(item => {
        if (item.patientId && item.patientId === updatedPatient.id) {
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      }));
    });

  }

  /** Pages to exhaustion, exactly as `loadMorbidityMortality` does. */
  loadRiskItems(): Observable<any[]> {
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    return this.getRiskItemsByGroup(this.groupId, this.currentPage, this.pageSize).pipe(
      mergeMap((res: any) => {
        const items = res?.data || [];
        const current = this.riskItemsSubject.getValue();
        const updated = [...current, ...items];
        this.riskItemsSubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadRiskItems();
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(() => {
        this.isLoading = false;
        return of([]);
      })
    );
  }

  /**
   * GET /api/risk-items/group/:groupId
   *
   * The register loads UNFILTERED and filters in the client. The server-side parameters exist
   * (D10 split the encryption so that they could) but the whole practice register is a few hundred
   * rows, and the moment a filter is pushed to the server the CLIENT-SIDE text search over the
   * decrypted `issue` would silently search only the filtered page. One source of truth for what
   * the doctor is looking at beats one round trip saved.
   */
  getRiskItemsByGroup(groupId: string, page: number, pageSize: number): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/group/${groupId}?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * POST /api/risk-items/mark-reported — D9 / H5.
   *
   * Called AFTER the download, with the ids the document actually covered, and only if the doctor
   * says yes. `groupId` is required and the server puts it in the `WHERE` of the write itself.
   *
   * The response message reports how many of the requested ids were actually marked; ids naming
   * another practice's rows or already-deleted rows are silently absent server-side, so a caller
   * that assumed "requested === marked" would tell the doctor the register was sent when part of
   * it was not.
   */
  markItemsReported(ids: string[]): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/mark-reported`, { groupId: this.groupId, ids }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════════════════
  //  H — THE REDACTED PDF EXPORT
  // ═══════════════════════════════════════════════════════════════════════════════════════

  /**
   * H1-H5 — the letterheaded risk register.
   *
   * jsPDF + `jspdf-autotable`, letterhead from `group.headerImageUrl` / `footerImageUrl`, header
   * and footer image handling copied from `pdf-download.service.ts:66-160`, table shape from
   * `patient-financal-billing.service.ts:275`.
   *
   * ── REDACTED BY DEFAULT (D3 / H2 / H8) ───────────────────────────────────────────────
   *
   * `options.redactNames` is read through `shouldRedactNames`, which returns TRUE for `undefined`.
   * A caller that never mentions redaction — the state of the toggle before anyone touches it —
   * gets the redacted document. Only an explicit `false` prints full names. See H8 in
   * `core/utils/redact-name.ts`; the failure mode of getting this backwards is a privacy leak onto
   * a document that has already left the practice, so it is a named function with its own spec
   * rather than an inline `if`.
   *
   * ── NOTHING ABOUT THIS PDF IS STORED (§7 / D9) ───────────────────────────────────────
   *
   * Deliberately unlike `TheatreDocumentExports`. The register is a summary of rows that stay
   * queryable; regenerating from the same filters reproduces it. The only fact the bytes carry
   * that the rows do not is WHEN IT WAS SENT — and that is `reportedAt` on each item, which H5
   * stamps through `markItemsReported`. One field, not a table plus a blob container plus an
   * erasure-order entry.
   *
   * @returns the ids of the items the document actually covered, for H5's offer. Empty if the
   *          export did not run (server-side render, or no items).
   */
  /**
   * The Patient and Patient No. cells for one exported row.
   *
   * THREE STATES, and the export must not collapse them — this document is handed to a hospital,
   * so a cell that is merely EMPTY is read as a statement of fact about the case.
   *
   *   patientId null            -> '—'. Genuinely not about a patient. A legacy shape: every
   *                                creation path now supplies one (2026-09-01), but the column is
   *                                still nullable so the case is still reachable.
   *   association not loaded    -> 'Not available'. There IS a patient; this payload just does
   *                                not carry them. Printing '—' here would tell a hospital the
   *                                issue had no patient, which is false. This is exactly what
   *                                `markItemsReported` used to cause before its includes were
   *                                fixed — it reached the SCREEN then, and would have reached
   *                                the PDF.
   *   loaded                    -> the redacted name (H2/D3), and the patient number unmasked.
   *
   * The NUMBER is never masked: §6.H is explicit that this is pseudonymisation and that the
   * doctor may need to trace a case when the hospital asks. Masking it would break the document's
   * own purpose while leaving hospital, ward and date in place anyway.
   */
  private riskPatientCells(item: any, redact: boolean): { label: string; number: string; unresolved: boolean } {
    if (item?.Patient) {
      return {
        label: redactedPatientLabel(item.Patient, redact),
        number: item.Patient.patientNumber || NO_NAME,
        unresolved: false,
      };
    }
    if (item?.patientId) {
      return { label: 'Not available', number: 'Not available', unresolved: true };
    }
    return { label: NO_NAME, number: NO_NAME, unresolved: false };
  }

  async exportRiskRegisterPdf(items: any[], options?: RiskRegisterExportOptions): Promise<string[]> {
    if (!isPlatformBrowser(this.platformId)) return [];

    const rows = (items || []).filter(item => !!item);
    if (!rows.length) return [];

    // Rows whose patient could not be resolved for the document. Should be zero — every creation
    // path supplies a patient and the group load carries the association — so a non-zero count
    // means a thin payload reached the export, and the doctor is told rather than handed a PDF
    // that quietly says 'Not available' where a name belongs.
    let unresolvedPatients = 0;

    const redact = shouldRedactNames(options?.redactNames);

    // ── D16 / §28 P7 — PHOTOGRAPHS ────────────────────────────────────────────────────────
    //
    // `=== true` and not a truthiness test: `undefined` must mean EXCLUDED, so an older caller
    // that never had the toggle cannot accidentally publish images. Same shape as
    // `shouldRedactNames`, for the same reason.
    const includeImages = options?.includeImages === true;

    // Every live photograph on every exported row, in the order the galleries show them. Collected
    // whether or not they are being embedded, because when they are NOT the document has to say
    // how many it withheld — an omission the reader cannot see is one they will assume never
    // happened, and "there were no photos" and "there were nine photos you are not being shown"
    // are very different things on a governance document.
    //
    // Each image carries its parent's `issue` forward as `riskIssue`, so the caption under the
    // photograph can name the row it evidences. The issue text is already printed in the table
    // above and contains no patient name, so this adds nothing the redaction would have masked.
    //
    // THE PDF ROW IS EXCLUDED, and this is not an omission — it is the same rule the
    // galleries follow (§29). A PDF attachment is stored TWICE over: the original document
    // as one row, and each rasterised page as its own image row carrying `originalPdfUrl`
    // back to it. Embedding the PDF row would try to draw a document as a bitmap, fail, and
    // report a photograph that "could not be retrieved" — while its pages sat in the same
    // document, rendered correctly. `mapMediaToFileObject`
    // (`core/interfaces/file-object.ts:25`) filters exactly this, for exactly this reason.
    //
    // It also keeps the counts honest: a two-page PDF is TWO photographs in this document,
    // not three, and the omitted-count sentence below must not claim otherwise.
    const exportImages = rows.flatMap(item => (Array.isArray(item.media) ? item.media : [])
      .filter((media: any) => media && media.active !== false && media.mediaUrl
        && String(media.mediaType || '').toLowerCase() !== 'application/pdf')
      .map((media: any) => ({ ...media, riskIssue: item.issue || '' })));

    const [jsPDFModule, autoTableModule] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    const { jsPDF } = jsPDFModule;
    const autoTable = (autoTableModule as any).default || autoTableModule;

    const group = this.groupService.group;
    const user = this.userService.user;

    const doc = new jsPDF({
      // Landscape: seven columns, one of which is a free-text issue that must stay readable
      // without wrapping to four lines per row.
      orientation: 'l',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const contentTop = 38;
    const contentBottom = 22;

    const logoImage = await this.getBase64ImageFromURL(group?.headerImageUrl);
    const footerImage = group?.footerImageUrl ? await this.getBase64ImageFromURL(group.footerImageUrl) : null;

    const generatedOn = moment().format('DD/MM/YYYY HH:mm');
    const doctor = user ? `${user.title || ''} ${user.name || ''} ${user.surname || ''}`.replace(/\s+/g, ' ').trim() : 'Unknown user';

    let y = contentTop;

    // ── Title block ──────────────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold').setFontSize(14).setTextColor('#353740');
    doc.text('FACILITY RISK REGISTER', pageWidth / 2, y, { align: 'center' });
    y += 6;

    doc.setFont('helvetica', 'normal').setFontSize(9);
    if (group?.name) {
      doc.text(String(group.name), pageWidth / 2, y, { align: 'center' });
      y += 5;
    }
    if (options?.periodLabel) {
      doc.text(options.periodLabel, pageWidth / 2, y, { align: 'center' });
      y += 5;
    }
    if (options?.filterSummary) {
      doc.text(options.filterSummary, pageWidth / 2, y, { align: 'center' });
      y += 5;
    }

    // Said on the document itself, in §6.H's words. A reader must be able to tell from the page
    // whether they are looking at masked names, and "Redacted" is the honest word — a row still
    // carries a hospital, a ward, a date and a patient number and is re-identifiable by design.
    doc.setFont('helvetica', 'italic').setFontSize(8);
    doc.text(
      redact
        ? 'Patient names are redacted. Rows remain re-identifiable by the practice from the patient number, facility and date.'
        : 'Patient names are shown in full at the exporting clinician\'s explicit request.',
      pageWidth / 2, y, { align: 'center' }
    );
    y += 5;

    // D16 — THE OMISSION IS STATED ON THE DOCUMENT, not just in the UI that produced it.
    //
    // The reader of this PDF is a hospital, and they cannot see the toggle the doctor did or did
    // not tick. Without this line an export that withheld nine photographs is indistinguishable
    // from a register that never had any — so "there are no photographs" would be inferred from
    // silence, which is exactly the assumption D16 exists to prevent. Printed in both directions,
    // so the document always accounts for the images either way.
    if (exportImages.length > 0) {
      doc.text(
        includeImages
          ? `${exportImages.length} attached ${exportImages.length === 1 ? 'photograph is' : 'photographs are'} included, following the table.`
          : `${exportImages.length} attached ${exportImages.length === 1 ? 'photograph has' : 'photographs have'} been omitted from this document.`,
        pageWidth / 2, y, { align: 'center' }
      );
      y += 5;
    }
    y += 2;

    // ── H3 — one table per hospital ──────────────────────────────────────────────────────
    const hospitalGroups = groupRiskItemsByHospital(rows);

    for (const hospitalGroup of hospitalGroups) {
      // A heading stranded at the foot of a page with its table overleaf reads as a different
      // hospital's data. Keep the heading, the summary and at least one row together.
      if (y + 24 > pageHeight - contentBottom) {
        doc.addPage();
        y = contentTop;
      }

      doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor('#353740');
      doc.text(hospitalGroup.title, margin, y);
      y += 5;

      // The per-hospital category summary line (H3). This is what makes the document governance
      // data rather than a list of complaints (D7): "17 consumable stock-outs at Hospital X this
      // month" is a conversation; sixty loose sentences is a shrug.
      doc.setFont('helvetica', 'normal').setFontSize(8.5);
      doc.text(riskCategorySummary(hospitalGroup.items), margin, y);
      y += 4;

      autoTable(doc, {
        startY: y,
        head: [['Date', 'Patient', 'Patient No.', 'Ward / Location', 'Category', 'Severity', 'Issue']],
        body: hospitalGroup.items.map(item => {
          // H2/D3 — redacted by default. Computed ONCE per row: see `riskPatientCells` for why
          // 'no patient' and 'patient not loaded' must not both render as an em dash on a
          // document a hospital reads.
          const patient = this.riskPatientCells(item, redact);
          if (patient.unresolved) unresolvedPatients++;
          return [
            item.dateOfIssue ? moment(item.dateOfIssue).format('DD/MM/YYYY') : NO_NAME,
            patient.label,
            patient.number,
            item.wardName || riskLocationLabel(item.location),
            riskCategoryLabel(item.category),
            riskSeverityLabel(item.severity),
            item.issue || '',
          ];
        }),
        theme: 'grid',
        styles: {
          font: 'helvetica',
          fontSize: 8,
          cellPadding: 1.5,
          overflow: 'linebreak',
          textColor: '#353740',
        },
        headStyles: {
          fillColor: [240, 240, 240],
          textColor: '#353740',
          fontStyle: 'bold',
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 38 },
          2: { cellWidth: 24 },
          3: { cellWidth: 34 },
          4: { cellWidth: 24 },
          5: { cellWidth: 18 },
          6: { cellWidth: 'auto' },
        },
        margin: { top: contentTop, bottom: contentBottom, left: margin, right: margin },
        tableWidth: pageWidth - margin * 2,
      });

      y = ((doc as any).lastAutoTable?.finalY || y) + 8;
    }

    // ── D16 / §28 P7 — the photographs, ONLY on an explicit opt-in ───────────────────────
    //
    // Placed HERE deliberately: after the last table, and BEFORE the header/footer loop below,
    // which walks `doc.getNumberOfPages()`. Any page this adds is therefore letterheaded and
    // numbered for free — an image page without the practice's letterhead would be an unattributed
    // clinical photograph in a document about a named hospital.
    if (includeImages && exportImages.length > 0) {
      await this.embedRiskImages(doc, exportImages, margin, contentTop, contentBottom);
    }

    // ── H4 — header and footer on every page ─────────────────────────────────────────────
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      await this.drawLetterheadHeader(doc, logoImage);
      await this.drawLetterheadFooter(doc, footerImage, doctor, generatedOn, page, totalPages);
    }

    // jsPDF's own `doc.save()` uses `<a download>` and is ignored by iOS WKWebView, which is why
    // `pdf-download.service.ts` routes through the platform-aware download service. Same here.
    const blob = doc.output('blob') as Blob;
    const filename = options?.filename || `risk-register-${moment().format('YYYY-MM-DD')}`;
    await this.fileDownloadService.download(blob, `${filename}.pdf`);

    if (unresolvedPatients > 0) {
      // Not a toast from here — this is a data service and the register owns user-facing
      // messaging. Logged so it is visible in a support trace, and the caller warns the doctor.
      console.warn('[risk register] ' + unresolvedPatients + ' exported row(s) had a patientId'
        + ' but no loaded Patient association; those cells read "Not available" rather than an'
        + ' em dash.');
    }

    return rows.map(item => item.id).filter((id: any) => !!id);
  }

  /** Centred letterhead image at the top of the current page. Copied from `pdf-download.service.ts:74`. */
  private drawLetterheadHeader(doc: any, logoImage: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!logoImage) {
        resolve();
        return;
      }
      const logoHeight = 24;
      const pageCenter = doc.internal.pageSize.getWidth() / 2;

      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const logoWidth = logoHeight * aspectRatio;
        doc.addImage(img, this.mimeToJsPdfFormat(this.parseDataUrlMime(logoImage)), pageCenter - logoWidth / 2, 5, logoWidth, logoHeight);
        resolve();
      };
      // A letterhead that will not load must not lose the doctor the whole register. The template
      // rejects here; this one resolves, and the page simply has no logo.
      img.onerror = () => resolve();
      img.src = logoImage;
    });
  }

  /**
   * H4 — the provenance line, so the hospital can cite the document back, plus page numbers and
   * the practice's footer image.
   *
   * WORDED EXACTLY LIKE EVERY OTHER EXPORT in this app:
   * `Generated by <doctor> using MedicAI (contact@medicai.co.za) on <date>`
   * — see `pdf-download.service.ts:143`. A governance document that a hospital receives
   * alongside letters and lab requisitions from the same practice should not be the one
   * that words its provenance differently; the contact address is also how the hospital
   * queries a document it does not recognise.
   */
  private drawLetterheadFooter(doc: any, footerImage: string | null, doctor: string, generatedOn: string, pageNumber: number, totalPages: number): Promise<void> {
    const writeText = () => {
      doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor('#353740');
      doc.text(`Generated by ${doctor} using MedicAI (contact@medicai.co.za) on ${generatedOn}`, 10, doc.internal.pageSize.getHeight() - 5);
      doc.text(`Page ${pageNumber} of ${totalPages}`, doc.internal.pageSize.getWidth() - 10, doc.internal.pageSize.getHeight() - 5, { align: 'right' });
    };

    return new Promise<void>((resolve) => {
      if (!footerImage) {
        writeText();
        resolve();
        return;
      }

      const footerImageHeight = 18;
      const pageCenter = doc.internal.pageSize.getWidth() / 2;

      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const footerImageWidth = footerImageHeight * aspectRatio;
        doc.addImage(
          img,
          this.mimeToJsPdfFormat(this.parseDataUrlMime(footerImage)),
          pageCenter - footerImageWidth / 2,
          doc.internal.pageSize.getHeight() - footerImageHeight - 7,
          footerImageWidth,
          footerImageHeight
        );
        writeText();
        resolve();
      };
      img.onerror = () => {
        writeText();
        resolve();
      };
      img.src = footerImage;
    });
  }

  /**
   * D16 / §28 P7 — lays the attached photographs out on fresh pages, two by two.
   *
   * ── WHY THIS IS NOT `PdfDownloadService.embedTimelineImagesInJsPdf` ──────────────────
   *
   * The §28 brief said image embedding "already exists — do not write a second one", and pointed
   * at `embedTimelineImagesInJsPdf` / `embedMediaAsGrid` in `pdf-download.service.ts`. Three facts
   * make that method unusable from here, and all three were checked rather than assumed:
   *
   *  1. `embedMediaAsGrid` IS NOT A FUNCTION. It is a boolean PARAMETER of
   *     `embedTimelineImagesInJsPdf` selecting single-column vs 2x2. There is one method, not two.
   *  2. That method is `private` on another service, so it cannot be called from here at all
   *     without editing `pdf-download.service.ts` — which is outside this workstream's boundary.
   *  3. Its geometry is hard-coded for the timeline's PORTRAIT A4 (`headerHeight`/`footerHeight`
   *     of 50mm each, sensible on a 297mm-tall page). The risk register is LANDSCAPE A4, 210mm
   *     tall, where the same constants would eat 100mm of a 210mm page. It also calls `addPage()`
   *     unconditionally and REJECTS on a single undecodable image, which would destroy an entire
   *     governance PDF because one SAS URL had expired.
   *
   * So what is reused is the layer that genuinely already exists AND is reachable: this service's
   * own `getBase64ImageFromURL`, `parseDataUrlMime` and `mimeToJsPdfFormat`. The base64 fetch, the
   * proxy call and the MIME handling are not reimplemented; only the page geometry is, because the
   * page is a different shape. See the P7 note in the report.
   *
   * ── ONE BAD IMAGE MUST NOT DESTROY THE DOCUMENT ──────────────────────────────────────
   *
   * `getBase64ImageFromURL` returns `''` rather than throwing (unlike the timeline's), and a
   * decode failure here RESOLVES instead of rejecting. A photograph whose blob has been deleted or
   * whose SAS token has expired is skipped and counted; the register still exports. The caption
   * under each image names the issue it belongs to, so an image separated from its row is still
   * attributable — a photograph of a broken instrument with no caption is evidence of nothing.
   */
  private async embedRiskImages(
    doc: any,
    images: any[],
    margin: number,
    contentTop: number,
    contentBottom: number
  ): Promise<void> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const gap = 6;
    const captionHeight = 5;

    const contentHeight = (pageHeight - contentBottom) - contentTop;
    const contentWidth = pageWidth - margin * 2;
    const cellW = (contentWidth - gap) / 2;
    const cellH = (contentHeight - gap) / 2;

    let slot = 0;
    let skipped = 0;

    for (const image of images) {
      const dataUrl = await this.getBase64ImageFromURL(image.mediaUrl);
      if (!dataUrl) { skipped++; continue; }

      // A new page for the first image and every fourth thereafter. Counted on IMAGES ACTUALLY
      // DRAWN, not on the loop index, so a skipped photograph does not leave a hole in the grid.
      if (slot % 4 === 0) {
        doc.addPage();
        doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor('#353740');
        doc.text('ATTACHED PHOTOGRAPHS', pageWidth / 2, contentTop - 4, { align: 'center' });
      }

      const cell = slot % 4;
      const cellLeft = margin + (cell % 2) * (cellW + gap);
      const cellTop = contentTop + Math.floor(cell / 2) * (cellH + gap);

      const drawn = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => {
          const nw = img.naturalWidth || img.width;
          const nh = img.naturalHeight || img.height;
          // Leave room under the image for its caption, and never upscale past the cell.
          const scale = Math.min(cellW / nw, (cellH - captionHeight) / nh);
          const drawW = nw * scale;
          const drawH = nh * scale;
          doc.addImage(
            img,
            this.mimeToJsPdfFormat(this.parseDataUrlMime(dataUrl)),
            cellLeft + (cellW - drawW) / 2,
            cellTop,
            drawW,
            drawH
          );

          // The caption is what makes an image evidence rather than decoration: it ties the
          // photograph back to the row in the table above it.
          const caption = String(image.riskIssue || '').trim();
          if (caption) {
            doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor('#353740');
            const line = doc.splitTextToSize(caption, cellW)[0];
            doc.text(line, cellLeft + cellW / 2, cellTop + drawH + 3.5, { align: 'center' });
          }
          resolve(true);
        };
        // Resolve, never reject — see the header. One unreadable photograph is a skipped cell,
        // not a failed export.
        img.onerror = () => resolve(false);
        img.src = dataUrl;
      });

      if (drawn) slot++; else skipped++;
    }

    if (skipped > 0) {
      // Stated on the page as well as logged: the reader must be able to tell that the count in
      // the header and the number of images they can see do not match for a reason.
      doc.setFont('helvetica', 'italic').setFontSize(8).setTextColor('#353740');
      doc.text(
        `${skipped} ${skipped === 1 ? 'photograph' : 'photographs'} could not be retrieved and ${skipped === 1 ? 'is' : 'are'} not shown.`,
        pageWidth / 2, pageHeight - contentBottom + 4, { align: 'center' }
      );
      console.warn('[risk register] ' + skipped + ' attached image(s) could not be embedded.');
    }
  }

  /** jsPDF expects short names like JPEG / PNG / WEBP. Copied from `pdf-download.service.ts:24`. */
  private mimeToJsPdfFormat(mime: string): string {
    const m = mime.toLowerCase();
    if (m.includes('jpeg') || m.includes('jpg')) return 'JPEG';
    if (m.includes('png')) return 'PNG';
    if (m.includes('webp')) return 'WEBP';
    return 'JPEG';
  }

  private parseDataUrlMime(dataUrl: string): string {
    const m = dataUrl.match(/^data:([^;]+);/);
    return m?.[1] || 'image/jpeg';
  }

  /**
   * The letterhead image as a data URL, through the server's image proxy. Copied from
   * `pdf-download.service.ts:516`, with one difference: a failure returns `''` instead of
   * throwing. A practice with no letterhead configured, or a proxy hiccup, must still get its
   * register.
   */
  private async getBase64ImageFromURL(url: string | undefined | null): Promise<string> {
    try {
      if (!url) {
        const response = await fetch('assets/full-logo.jpg');
        if (!response.ok) return '';
        const blob = await response.blob();
        return await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => resolve('');
          reader.readAsDataURL(blob);
        });
      }
      const params = new URLSearchParams();
      params.set('url', url);
      const response = await fetch(`${environment.API_URL}/api/image-proxy?${params.toString()}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return '';
      const base64Image = (data as { base64Image?: string }).base64Image;
      if (!base64Image) return '';
      const mimeType = (data as { mimeType?: string }).mimeType || 'image/jpeg';
      return `data:${mimeType};base64,${base64Image}`;
    } catch {
      return '';
    }
  }
}

/** Options for `exportRiskRegisterPdf`. Every field is optional; see H8 for why `redactNames` is. */
export interface RiskRegisterExportOptions {
  /**
   * H8 — REDACTION IS THE DEFAULT. `undefined` (the toggle was never touched) means REDACTED.
   * Only an explicit `false` prints full patient names.
   */
  redactNames?: boolean;
  /**
   * D16 / §28 P7 — PHOTOGRAPHS ARE EXCLUDED UNLESS THIS IS EXPLICITLY `true`.
   *
   * `undefined` (an older caller, or one that never had the toggle) means EXCLUDED, exactly as
   * `redactNames: undefined` means redacted. The default is the safe one because the risk is a
   * privacy leak onto a document that has already left the practice: a ward photograph can
   * incidentally carry a wristband, a chart, a whiteboard or a face, which the name masking cannot
   * touch.
   *
   * ALL-OR-NOTHING, never per-image — see `includeImages` on `RiskManagementListComponent`.
   *
   * When this is false the export does NOT silently drop the photographs: it prints how many it
   * withheld. That printed count is half of D16 and must not be removed.
   */
  includeImages?: boolean;
  /** e.g. "01/03/2026 to 31/03/2026". Printed under the title. */
  periodLabel?: string;
  /** e.g. "Category: Consumables · Severity: High". Printed under the period. */
  filterSummary?: string;
  /** Without the `.pdf` extension. */
  filename?: string;
}

/** One hospital's slice of the register — for the PDF (H3) and for the on-screen grouping (G2). */
export interface RiskHospitalGroup {
  key: string;
  title: string;
  /**
   * True when `hospitalId` was NULL and this group is held together by `hospitalName` alone.
   * Legitimate and common — see `riskHospitalGroupKey`.
   */
  nameOnly: boolean;
  items: any[];
}

/**
 * The grouping key for H3, and the single most easily got-wrong line in this workstream.
 *
 * ── `hospitalId` IS FREQUENTLY NULL WHILE `hospitalName` IS SET, LEGITIMATELY ─────────────
 *
 * §4 branch 3 resolves the facility from `PatientAdmissionHistory`, which stores the hospital as a
 * PLAIN STRING (`models/patient-admission-history.js:42`), and matches that string back to a
 * `Hospitals` row by exact name. Workstream D measured the real miss rate against live data and
 * found pairs like `Olivedale` (as written at discharge) against `Netcare Olivedale` (the
 * `Hospitals` row) — a near-certain same-facility pair that correctly resolves to
 * `hospitalId: NULL` with the name intact. Exact matching is the right call there: a fuzzy match
 * confusing `Life Bedford Gardens` with `Life Bedfordview` would file a complaint against an
 * uninvolved hospital.
 *
 * So a NULL `hospitalId` with a name is NOT an unresolved row. Grouping by `hospitalId` alone
 * would either DROP those rows from the register or lump every hospital that failed the name match
 * into one "Unknown" table — the plan's Review under workstream D calls this out by name. This
 * function therefore falls back to the NAME, normalised only by trim and case (which is also what
 * the server's matcher does, and what makes the live `'Akasia '` rows with a trailing space group
 * with `'Akasia'`).
 *
 * Only when there is NO name either does it fall back to `location` — and that is the `'rooms'`
 * case, an in-rooms/OPD issue, which D6 exists to make a FIRST-CLASS OUTCOME rather than a
 * resolution failure. It is titled "In-rooms / OPD", never "Unknown".
 */
export function riskHospitalGroupKey(item: any): string {
  if (item?.hospitalId) return `id:${item.hospitalId}`;
  const name = String(item?.hospitalName ?? '').trim();
  if (name) return `name:${name.toLowerCase()}`;
  return `location:${item?.location || 'rooms'}`;
}

/** The heading for a group. Prefers the D5 SNAPSHOT, which survives a rename or a deactivation. */
export function riskHospitalGroupTitle(item: any): string {
  const snapshot = String(item?.hospitalName ?? '').trim();
  if (snapshot) return snapshot;
  const joined = String(item?.hospital?.name ?? '').trim();
  if (joined) return joined;
  return riskLocationLabel(item?.location);
}

/**
 * Groups the register by facility for H3 and G2.
 *
 * Named facilities sort alphabetically together, whether or not their `hospitalId` resolved — an
 * `Olivedale` group and a `Netcare Olivedale` group are two rows of the register, which is the
 * honest rendering of what the data says. Groups with no facility name at all (the in-rooms /
 * OPD rows) sort last, because a document handed to a hospital leads with that hospital.
 */
export function groupRiskItemsByHospital(items: any[]): RiskHospitalGroup[] {
  const groups = new Map<string, RiskHospitalGroup>();

  for (const item of items || []) {
    if (!item) continue;
    const key = riskHospitalGroupKey(item);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        title: riskHospitalGroupTitle(item),
        nameOnly: !item.hospitalId && !!String(item.hospitalName ?? '').trim(),
        items: [],
      };
      groups.set(key, group);
    }
    group.items.push(item);
  }

  return Array.from(groups.values()).sort((a, b) => {
    const aUnnamed = a.key.startsWith('location:');
    const bUnnamed = b.key.startsWith('location:');
    if (aUnnamed !== bUnnamed) return aUnnamed ? 1 : -1;
    return a.title.localeCompare(b.title);
  });
}

/**
 * The per-hospital summary line (H3): a total, then a count per D7 category in taxonomy order.
 *
 * Taxonomy order rather than count order, deliberately — the same hospital's line then reads the
 * same shape month to month, and a category going from 1 to 9 is visible in place instead of
 * jumping to the front of the line.
 */
export function riskCategorySummary(items: any[]): string {
  const counts = new Map<string, number>();
  for (const item of items || []) {
    const category = String(item?.category ?? '').toLowerCase() || 'uncategorised';
    counts.set(category, (counts.get(category) || 0) + 1);
  }

  const ordered: string[] = [];
  for (const category of RISK_CATEGORIES) {
    const count = counts.get(category);
    if (count) {
      ordered.push(`${riskCategoryLabel(category)} ${count}`);
      counts.delete(category);
    }
  }
  // Anything off-taxonomy (an older row, a hand-added value) is still counted rather than lost.
  for (const [category, count] of counts) {
    ordered.push(`${riskCategoryLabel(category)} ${count}`);
  }

  const total = (items || []).length;
  const noun = total === 1 ? 'issue' : 'issues';
  return ordered.length ? `${total} ${noun} — ${ordered.join(', ')}` : `${total} ${noun}`;
}

/** Re-exported so a caller does not have to reach into two files to render one register row. */
export { redactName, redactedPatientLabel, shouldRedactNames };
